import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, Input, Row, Col, Button, Popover, Alert, Spin } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import {
  CustomCollapseCard,
  CustomForm,
  CustomFormItem,
  CustomImage,
  CustomButton,
  Text,
  useText,
} from '@src/components';
import {
  getVehicleDevicePlateValidation,
  setAllPlatesArray,
  setAllPlatesDetails,
} from '@src/store/slice/addNewCarStore/addCarPlateInformationSlice';
import { incrementActiveStep } from '@src/store/slice/addNewCarStore/addCarTypeSlice';
import toNumber from 'lodash/toNumber';
import replace from 'lodash/replace';
import closeInfo from '@src/assets/icons/ticketcar/close.svg';
import warning from '@src/assets/icons/icon-profil-warning.svg';
import '@src/styles/addNewCar/addCarPlateInformation.scss';
import downloadIcon from '@src/assets/icons/icon-download.svg';
import excelIcon from '@src/assets/icons/icon-excel.svg';
import uploadIcon from '@src/assets/icons/icon-upload.svg';
import download from 'js-file-download';
import dayjs from '@src/utils/day';
import { getFileNameByLanguage } from '@src/utils/utils';
import UploadFileModal from '@src/pages/ticketRestaurant/cardOrders/cardInformation/UploadFileModal';
import { errorDialog } from '@src/components/CustomDialog';
import addCarPlateInformationServices from '@src/services/addNewCar/addCarPlateInformation.services';
import CustomTooltip from '@src/components/CustomTooltip';
import iconWarning from '@src/assets/icons/icon-info.svg';
import iconKapat from '@src/assets/icons/icn-kapat.svg';
import { useLang } from '@src/context';
import { useLanguageChange } from '@src/hooks/useLanguageChange';

const AddCarPlateInformation = () => {
  const dispatch = useDispatch();
  const { language } = useLang();
  const languageRef = useRef(language);
  languageRef.current = language;
  const [form] = Form.useForm();
  const [plateFields, setPlateFields] = useState([
    { id: 1, name: 'plate1' },
    { id: 2, name: 'plate2' },
    { id: 3, name: 'plate3' },
  ]);
  const { allPlatesArray, allPlatesDetails } = useSelector(
    (state) => state?.addCarPlateInformation,
  );

  const {
    vehicleFuelTypes = [],
    vehicleDepartments = [],
    vehicleCostCenters = [],
    vehicleTypes = [],
    vehicleAttributes = [],
    vehicleLimitTypesDayRestrictionOperations = [],
    vehicleLimitValueTypes = [],
    vehicleLAddressGetList = [],
  } = useSelector((state) => state.addCarPlateDetail);

  const [errors, setErrors] = useState({});
  const [messages, setMessages] = useState({});
  const [closeTooltip, setCloseTooltip] = useState(false);
  const [goToPlateDetail, setGoToPlateDetail] = useState(false);
  const [canAddPlate, setCanAddPlate] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [debounceTimers, setDebounceTimers] = useState({});
  const [expandedFields, setExpandedFields] = useState({});
  const [platesArray, setPlatesArray] = useState([]);
  const [platesDetails, setPlatesDetails] = useState([]);
  const [loadingFields, setLoadingFields] = useState({});
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const { selectedCustomer } = useSelector((state) => state?.customer);
  const { isAdmin } = useSelector((state) => state.auth);
  const { selectedUserId } = useSelector((state) => state.user);
  const { selectVehicleDeviceTypeList } = useSelector((state) => state?.addCarType);

  const duplicatePlateError = useText('duplicatePlateError');
  const invalidPlateError = useText('invalidPlateError');
  const completeAllPlateInformation = useText('completeAllPlateInformation');
  const plateLabel = useText('plate');
  /**
   * `handlePlateChange` below is a `useCallback(debounce(...), [dispatch])` - it's only ever
   * created once (dispatch never changes), so its closure would otherwise freeze these translated
   * strings at whatever language was active on the very first render. Refs let the debounced
   * closure always read the current translation without needing to be recreated.
   */
  const duplicatePlateErrorRef = useRef(duplicatePlateError);
  duplicatePlateErrorRef.current = duplicatePlateError;
  const invalidPlateErrorRef = useRef(invalidPlateError);
  invalidPlateErrorRef.current = invalidPlateError;
  const errorsRef = useRef(errors);
  errorsRef.current = errors;

  useEffect(() => {
    form.setFields(
      Object.entries(errors).map(([name, error]) => ({
        name,
        errors: error
          ? [
              error === 'duplicatePlateError'
                ? duplicatePlateError
                : error === 'invalidPlateError'
                  ? invalidPlateError
                  : error,
            ]
          : [],
      })),
    );
  }, [form, errors, duplicatePlateError, invalidPlateError]);

  useEffect(() => {
    checkCanAddPlate();
  }, [form, plateFields, errors, messages, canAddPlate, loadingFields]);

  useEffect(() => {
    dispatch(setAllPlatesArray(platesArray));
  }, [platesArray, dispatch]);

  useEffect(() => {
    dispatch(setAllPlatesDetails(platesDetails));
  }, [platesDetails, dispatch]);

  useEffect(() => {
    if (allPlatesDetails?.length > 0 && allPlatesArray?.length > 0 && goToPlateDetail) {
      setGoToPlateDetail(false);
      handleCarApproval();
    }
  }, [allPlatesArray, allPlatesDetails, goToPlateDetail]);

  const checkCanAddPlate = () => {
    const values = form.getFieldsValue();
    const isLoading = Object.values(loadingFields).some((loading) => loading);
    const filledFieldsValid = plateFields.every((field) => {
      const value = values[field.name];
      const noError = !errors[field.name];
      const noMessage = !messages[field.name];

      if (field.name !== 'plate1') {
        return !value || (noError && noMessage) || (value && noError && noMessage);
      }

      return value && noError && noMessage;
    });

    const isButtonEnabled = filledFieldsValid && !isLoading;

    setCanAddPlate(isButtonEnabled);
    return isButtonEnabled;
  };

  const checkDuplicatePlates = (plate, name) => {
    const values = form.getFieldsValue();
    for (let key in values) {
      if (key !== name && values[key] === plate) {
        return true;
      }
    }
    return false;
  };

  const handleAddPlate = () => {
    const nextIndex = plateFields.length + 1;
    const newPlate = { id: nextIndex, name: `plate${nextIndex}` };
    setPlateFields([...plateFields, newPlate]);
    checkCanAddPlate();
    setShowWarning(false);
  };

  const handleRemovePlate = (name) => {
    const newPlateFields = plateFields.filter((field) => field.name !== name);
    setPlateFields(newPlateFields);
    form.resetFields([name]);
    setErrors({ ...errors, [name]: null });
    setMessages({ ...messages, [name]: null });
    setPlatesArray(platesArray.filter((plate) => plate.name !== name));
    checkCanAddPlate();
  };

  const handleValidationSuccess = (name, message) => {
    form.setFields([{ name, errors: [] }]);
    setErrors((prevErrors) => ({ ...prevErrors, [name]: null }));
    setMessages((prevMessages) => ({ ...prevMessages, [name]: message }));
    setLoadingFields((prev) => ({ ...prev, [name]: false }));
  };

  const handleValidationError = (name, error) => {
    form.setFields([{ name, errors: [error.message] }]);
    setErrors((prevErrors) => ({ ...prevErrors, [name]: error.message }));
    setMessages((prevMessages) => ({ ...prevMessages, [name]: null }));
    setLoadingFields((prev) => ({ ...prev, [name]: false }));
  };

  const debouncedDispatch = useCallback(
    (value, name) => {
      clearTimeout(debounceTimers[name]);
      const newTimers = { ...debounceTimers };
      newTimers[name] = setTimeout(() => {
        if (/^(0[1-9]|[1-7]\d|8[0-1])[A-Z]{1,3}\d{1,4}$/.test(value)) {
          const requestLanguage = languageRef.current;
          setLoadingFields((prev) => ({ ...prev, [name]: true }));
          dispatch(getVehicleDevicePlateValidation({ plate: value }))
            .then((response) => {
              if (requestLanguage !== languageRef.current) return;
              const message = response?.payload?.data?.message || null;
              if (response?.error) {
                handleValidationError(name, response.error);
              } else {
                handleValidationSuccess(name, message);
              }
            })
            .catch((error) => {
              if (requestLanguage !== languageRef.current) return;
              handleValidationError(name, error);
            });
        }
      }, 500);
      setDebounceTimers(newTimers);
    },
    [debounceTimers, dispatch],
  );

  useLanguageChange(() => {
    const values = form.getFieldsValue();
    plateFields.forEach(({ name }) => {
      const value = values[name];
      if (
        /^(0[1-9]|[1-7]\d|8[0-1])[A-Z]{1,3}\d{1,4}$/.test(value || '') &&
        !checkDuplicatePlates(value, name)
      ) {
        debouncedDispatch(value, name);
      }
    });
  });

  const handlePlateChange = useCallback(
    debounce((e, name) => {
      const { value } = e.target;
      const formattedValue = value.replace(/[^A-Z0-9]/g, '');
      form.setFieldsValue({ [name]: formattedValue });

      const regex = /^(0[1-9]|[1-7]\d|8[0-1])[A-Z]{1,3}\d{1,4}$/;
      const validLengths = [6, 7, 8, 9];

      const isDuplicate = checkDuplicatePlates(formattedValue, name);

      let newErrors = { ...errorsRef.current };
      if (formattedValue === '') {
        form.setFields([{ name, errors: [] }]);
        newErrors[name] = null;
        setMessages((prevMessages) => ({ ...prevMessages, [name]: null }));
        setPlatesArray((prevArray) => prevArray.filter((plate) => plate.name !== name));
      } else if (isDuplicate) {
        form.setFields([{ name, errors: [duplicatePlateErrorRef.current] }]);
        newErrors[name] = 'duplicatePlateError';
        setMessages((prevMessages) => ({ ...prevMessages, [name]: null }));
      } else {
        newErrors[name] = null;
        if (
          validLengths.includes(formattedValue.length) &&
          regex.test(formattedValue) &&
          !checkDuplicatePlates(formattedValue, name)
        ) {
          setErrors(newErrors);
          debouncedDispatch(formattedValue, name);
        } else if (formattedValue.length > 0 && !regex.test(formattedValue)) {
          form.setFields([{ name, errors: [invalidPlateErrorRef.current] }]);
          newErrors[name] = 'invalidPlateError';
          setMessages((prevMessages) => ({ ...prevMessages, [name]: null }));
        } else {
          form.setFields([{ name, errors: [] }]);
        }
      }

      for (let key in newErrors) {
        if (
          newErrors[key] === 'duplicatePlateError' &&
          !checkDuplicatePlates(form.getFieldValue(key), key)
        ) {
          form.setFields([{ name: key, errors: [] }]);
          newErrors[key] = null;
        }
      }
      setErrors(newErrors);

      setPlatesArray((prevArray) => {
        const existingPlate = prevArray.find((plate) => plate.name === name);
        if (existingPlate) {
          if (formattedValue) {
            return prevArray.map((plate) =>
              plate.name === name ? { ...plate, plate: formattedValue } : plate,
            );
          } else {
            return prevArray.filter((plate) => plate.name !== name);
          }
        } else {
          if (formattedValue) {
            const plateNumber = toNumber(replace(name, /\D/g, ''));
            const newPlate = {
              id: plateNumber,
              name,
              plate: formattedValue,
            };
            return [...prevArray, newPlate];
          }
          return prevArray;
        }
      });

      checkCanAddPlate();
      setShowWarning(false);
    }, 1000),
    [dispatch],
  );

  const handleAlertClose = () => {
    setShowWarning(false);
  };

  const handleButtonClick = () => {
    form
      .validateFields()
      .then(() => {
        const allPlatesValid = checkCanAddPlate();

        if (!allPlatesValid) {
          setShowWarning(true);
          setTimeout(() => setShowWarning(false), 3000);
        } else {
          handleAddPlate();
        }
      })
      .catch((info) => {
        setShowWarning(true);
        setTimeout(() => setShowWarning(false), 3000);
      });
  };

  const toggleExpansion = (name) => {
    setExpandedFields((prevState) => ({
      ...prevState,
      [name]: !prevState[name],
    }));
  };

  const handleCarApproval = useCallback(async () => {
    console.log('step 2');
    dispatch(incrementActiveStep());
  }, [dispatch]);

  const handleDownloadExcel = useCallback(async () => {
    try {
      const data = await addCarPlateInformationServices.createVehicleExcelTemplate({
        customerId: isAdmin && selectedUserId ? selectedUserId : selectedCustomer.customerId,
        deviceTypeId: selectVehicleDeviceTypeList?.deviceTypeId,
        vouId: 4,
      });
      download(
        data,
        `${getFileNameByLanguage('yeni-arac-ekle', 'add-new-vehicle')}-${dayjs().format(
          'DD-MM-YYYY hh:mm',
        )}.xlsx`,
      );
    } catch (error) {
      errorDialog({
        title: <Text t="error" />,
        message: error?.statusText,
      });
    }
  }, [selectedCustomer?.customerId, selectVehicleDeviceTypeList?.deviceTypeId]);

  return (
    <div
      className="add-car-plateInformation-content"
      data-testid="add-car-plate-information-component"
    >
      {allPlatesDetails?.length <= 0 && plateFields?.length > 3 && !closeTooltip ? (
        <button
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            display: 'flex',
            zIndex: 999,
            width: '100%',
            height: '100%',
            opacity: 0.7,
            backgroundColor: '#0e0e17',
            cursor: 'default',
          }}
          onClick={() => setCloseTooltip(true)}
        />
      ) : null}
      <div className="add-car-plate-card-information-header-tools">
        <span className="info-text">
          <Text t="youCanEasilyFillInLicensePlateInformation" />
        </span>

        <CustomTooltip
          placement="bottom"
          overlayClassName="add-plate-custom-tooltip"
          overlayInnerStyle={{
            backgroundColor: '#fff',
            width: '300px',
            height: '80px',
            borderRadius: '8px',
          }}
          title={
            <div style={{ display: 'flex' }}>
              <CustomImage src={iconWarning} />
              <span
                style={{
                  color: '#3c4655',
                  marginRight:
                    allPlatesDetails?.length <= 0 && plateFields?.length > 3 && !closeTooltip
                      ? 30
                      : 0,
                }}
              >
                <Text t="insteadOfManualEntryUploadLicensePlatesIn" />
              </span>
              {allPlatesDetails?.length <= 0 && plateFields?.length > 3 && !closeTooltip ? (
                <CustomImage
                  src={iconKapat}
                  onClick={() => setCloseTooltip(true)}
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    position: 'absolute',
                    right: -40,
                    top: 10,
                  }}
                />
              ) : null}
            </div>
          }
          visible={allPlatesDetails?.length <= 0 && plateFields?.length > 3 && !closeTooltip}
          trigger={'hover'}
        >
          <CustomButton
            id="ticketCar-cardOrders-cardInformation-cardDetailInformation-download-btn"
            className="download-btn"
            style={{
              cursor:
                allPlatesDetails?.length <= 0 && plateFields?.length > 3 && !closeTooltip
                  ? 'default'
                  : 'pointer',
              zIndex:
                allPlatesDetails?.length <= 0 && plateFields?.length > 3 && !closeTooltip ? 999 : 1,
            }}
            onClick={() =>
              allPlatesDetails?.length <= 0 && plateFields?.length > 3 && !closeTooltip
                ? null
                : handleDownloadExcel()
            }
          >
            <CustomImage src={downloadIcon} />
            <Text t="downloadExcelTemplate" />
          </CustomButton>
        </CustomTooltip>
        <CustomButton
          id="ticketCar-cardOrders-cardInformation-cardDetailInformation-upload-btn"
          className="upload-btn"
          onClick={() =>
            allPlatesDetails?.length <= 0 && plateFields?.length > 3 && !closeTooltip
              ? null
              : setUploadModalVisible(true)
          }
          style={{
            cursor:
              allPlatesDetails?.length <= 0 && plateFields?.length > 3 && !closeTooltip
                ? 'default'
                : 'pointer',
            zIndex:
              allPlatesDetails?.length <= 0 && plateFields?.length > 3 && !closeTooltip ? 999 : 1,
          }}
        >
          <CustomImage src={uploadIcon} />
          <Text t="uploadWithExcel" />
          <CustomImage src={excelIcon} />
        </CustomButton>
      </div>
      <Row justify={'center'} className="header-row-title">
        <span>
          <Text t="licensePlateInformation" />
        </span>
      </Row>
      <CustomCollapseCard>
        <div className="monthly-order-container">
          <span className="warning-title">
            <Text t="pleaseEnterTheLicensePlatesYouWantTo" />
          </span>
          {showWarning && (
            <Alert
              message={
                <div className="alert-message-contain">
                  <CustomImage src={warning} />{' '}
                  <div className="description">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: completeAllPlateInformation,
                      }}
                    />
                  </div>
                </div>
              }
              banner
              showIcon={false}
              className="custom-alert-warning"
              closable
              onClose={handleAlertClose}
            />
          )}
          <CustomForm
            name="monthlyOrderForm"
            className="monthly-order-form"
            initialValues={{}}
            autoComplete="off"
            layout={'horizontal'}
            form={form}
          >
            {plateFields.map((field, index) => (
              <div key={field.id} className="form-item">
                <div className="monthly-amount-content">
                  <Row gutter={8} align="middle" justify="center">
                    <Col flex="auto">
                      <Popover
                        content={
                          messages[field.name] && (
                            <div className="error-popover-content-car-plate-inforn">
                              <CustomImage
                                src={closeInfo}
                                style={{ width: '36px', marginRight: '8px', color: 'red' }}
                              />
                              <div
                                className={expandedFields[field.name] ? 'expanded' : 'collapsed'}
                              >
                                {expandedFields[field.name]
                                  ? messages[field.name]
                                  : messages[field.name]?.substring(0, 77) + '...'}
                                {messages[field.name]?.length > 77 && (
                                  <button
                                    onClick={() => toggleExpansion(field.name)}
                                    style={{
                                      color: 'blue',
                                      cursor: 'pointer',
                                      background: 'none',
                                      border: 'none',
                                      padding: 0,
                                    }}
                                  >
                                    {expandedFields[field.name] ? ' Kapat' : ' Devamını Oku'}
                                  </button>
                                )}
                              </div>
                            </div>
                          )
                        }
                        visible={!!messages[field.name]}
                        placement="right"
                        arrowPointAtCenter
                        overlayClassName="custom-popover"
                        getPopupContainer={(trigger) => trigger.parentElement}
                      >
                        <CustomFormItem
                          label={`${plateLabel} ${field.id}`}
                          name={field.name}
                          className="monthly-amount-form-item"
                        >
                          <div
                            style={{ display: 'flex', alignItems: 'center', position: 'relative' }}
                          >
                            <Input
                              data-testid={`plate${index + 1}-input`}
                              className={`box border-left ${
                                errors[field.name] || messages[field.name] ? 'input-error' : ''
                              }`}
                              onInput={(e) => (e.target.value = e.target.value.toUpperCase())}
                              onChange={(e) => handlePlateChange(e, field.name)}
                              maxLength={8}
                              style={{ flex: '1 1 auto' }}
                            />
                            {loadingFields[field.name] && (
                              <Spin
                                size="small"
                                className="custom-spin"
                                style={{
                                  position: 'absolute',
                                  right: '-24px',
                                  top: '24px',
                                  color: '#d52b1e',
                                }}
                              />
                            )}
                          </div>
                        </CustomFormItem>
                      </Popover>
                    </Col>
                    <Col className="delete-button-col">
                      {index === plateFields.length - 1 && plateFields.length > 1 && (
                        <Button
                          type="text"
                          icon={
                            <DeleteOutlined
                              style={{ fontSize: '24px', marginTop: '-14px', marginRight: '34px' }}
                            />
                          }
                          onClick={() => handleRemovePlate(field.name)}
                          className="delete-button"
                        />
                      )}
                    </Col>
                  </Row>
                </div>
              </div>
            ))}
            <div className="line-style">
              <div style={{ display: 'inline-block' }}>
                <Button
                  id="car-primary-btn"
                  type="primary"
                  htmlType="button"
                  className="new-vehicle-style"
                  onClick={handleButtonClick}
                >
                  <span className="new-car-row">
                    <PlusOutlined style={{ fontSize: '10px' }} />
                    <Text t="addNewLicensePlate" />
                  </span>
                </Button>
              </div>
            </div>
          </CustomForm>
        </div>
      </CustomCollapseCard>
      <div className="step-btn-container" style={{ marginTop: '30px' }}>
        <Button
          id="add-new-car-container-next-btn"
          className="next-btn"
          onClick={handleCarApproval}
          disabled={!canAddPlate}
        >
          <Text t="continue" />
        </Button>
      </div>

      <UploadFileModal
        modalVisible={uploadModalVisible}
        handleModalVisible={setUploadModalVisible}
        addNewPlate={true}
        customerId={isAdmin && selectedUserId ? selectedUserId : selectedCustomer.customerId}
        deviceTypeId={selectVehicleDeviceTypeList?.deviceTypeId}
        setExcelPlatesArray={(array) => {
          setGoToPlateDetail(true);
          setPlatesArray(array.platesArray);
          setPlatesDetails(array.data);
        }}
        vehicleFuelTypes={vehicleFuelTypes}
        vehicleDepartments={vehicleDepartments}
        vehicleCostCenters={vehicleCostCenters}
        vehicleTypes={vehicleTypes}
        vehicleAttributes={vehicleAttributes}
        vehicleLimitTypesDayRestrictionOperations={vehicleLimitTypesDayRestrictionOperations}
        vehicleLimitValueTypes={vehicleLimitValueTypes}
        vehicleLAddressGetList={vehicleLAddressGetList}
      />
    </div>
  );
};

export default AddCarPlateInformation;
